import isEqual from 'lodash.isequal'

import {ExceptionType, UsersGroupsOption} from 'interfaces'
import {
  CACAssignment,
  CACAssignments,
  CACRestriction,
  CacIdpAdditionalControls,
  CloudAppAdditionalControls,
  CloudAppListTypes,
  CloudAppResponseType,
  CloudAppSaveApp,
  CloudAppSaveList,
  CloudAppType,
} from 'interfaces/Policy'

import {
  CAC_GLOBAL_SETTINGS_APP_IDS,
  CAC_GLOBAL_SETTINGS_CONFIG,
  CacGlobalSettingsAppId,
} from './cloudApp.constant'

export const supportsCacGlobalSettings = (
  appId: string,
): appId is CacGlobalSettingsAppId =>
  (CAC_GLOBAL_SETTINGS_APP_IDS as readonly string[]).includes(appId)

/** Warning is only shown when the CAC warning flag is on; otherwise it reads as Allow. */
export const getCacDisplayRestriction = (
  restriction: CACRestriction | undefined,
  canWarn: boolean,
): CACRestriction => {
  const resolvedRestriction = restriction ?? CACRestriction.Allow
  if (!canWarn && resolvedRestriction === CACRestriction.Warning) {
    return CACRestriction.Allow
  }
  return resolvedRestriction
}

/** Click order for an AI control: Allow → Block → Warning (warning flag on only) → Allow. */
export const getNextCacRestriction = (
  restriction: CACRestriction,
  canWarn: boolean,
): CACRestriction => {
  if (restriction === CACRestriction.Allow) {
    return CACRestriction.Block
  }
  if (restriction === CACRestriction.Block && canWarn) {
    return CACRestriction.Warning
  }
  return CACRestriction.Allow
}

/** Personal AI can differ from work only while sign-in is allowed and AI is not blocked. */
export const isConsumerAiConfigurable = (
  loginAllowed: boolean,
  aiRestriction?: CACRestriction,
): boolean => loginAllowed && aiRestriction !== CACRestriction.Block

export type PersonalAiReason =
  | 'own'
  | 'inherited'
  | 'signInBlocked'
  | 'aiBlocked'

/** How the personal AI row reads, including why it is locked or copied from work. */
export const getPersonalAiDisplay = (
  controls: CacIdpAdditionalControls,
  aiDisplayRestriction: CACRestriction,
): {restriction: CACRestriction; reason: PersonalAiReason} => {
  if (!controls.consumer_login_allowed) {
    return {restriction: CACRestriction.Block, reason: 'signInBlocked'}
  }
  if (aiDisplayRestriction === CACRestriction.Block) {
    return {restriction: CACRestriction.Block, reason: 'aiBlocked'}
  }
  if (controls.consumer_ai_allowed === false) {
    return {restriction: CACRestriction.Block, reason: 'own'}
  }
  // Personal AI is Allowed or Blocked. Warning on the work control counts as allowed.
  return {restriction: CACRestriction.Allow, reason: 'inherited'}
}

/** Screen-reader note announced after personal sign-in is toggled. */
export const getSignInCascadeKey = (
  loginAllowed: boolean,
  aiRestriction: CACRestriction,
  consumerAiAllowed: boolean | undefined,
): string => {
  if (!loginAllowed) return 'policies:CAC_SETTINGS_NOTE_SIGN_IN_BLOCKED'
  if (aiRestriction === CACRestriction.Block) {
    return 'policies:CAC_SETTINGS_NOTE_AI_BLOCKED'
  }
  if (consumerAiAllowed === false) {
    return 'policies:CAC_SETTINGS_NOTE_SIGN_IN_ALLOWED_AI_BLOCKED'
  }
  return 'policies:CAC_SETTINGS_NOTE_SIGN_IN_ALLOWED'
}

/** The only way the contract expresses "everyone on the policy". */
export const CAC_ASSIGNMENT_ALL = 'all' as const

export type CacValueAssignment = CACAssignment | typeof CAC_ASSIGNMENT_ALL

export const getCacConfiguredValues = (app?: CloudAppType): string[] =>
  Object.keys(app?.assignments ?? {})

export const getCacValueAssignment = (
  app: CloudAppType | undefined,
  value: string,
): CacValueAssignment => app?.assignments?.[value] ?? CAC_ASSIGNMENT_ALL

export const isAssignedToEveryone = (
  assignment: CacValueAssignment,
): assignment is typeof CAC_ASSIGNMENT_ALL => assignment === CAC_ASSIGNMENT_ALL

/** True only when identities are actually selected, not an empty draft. */
export const cacValueHasRestrictedAccess = (
  assignment: CacValueAssignment,
): boolean =>
  !isAssignedToEveryone(assignment) &&
  (assignment.users.length > 0 || assignment.groups.length > 0)

/**
 * The wire carries bare emails, so `type` is inferred from which list an
 * identity came from and `name` is left for the caller to fall back on.
 */
export const toIdentityOptions = (
  assignment: CacValueAssignment,
): UsersGroupsOption[] => {
  if (isAssignedToEveryone(assignment)) {
    return []
  }

  const toOptions = (emailIds: string[], type: ExceptionType) =>
    Array.from(new Set(emailIds)).map(emailId => ({emailId, name: '', type}))

  return [
    ...toOptions(assignment.users, ExceptionType.User),
    ...toOptions(assignment.groups, ExceptionType.Group),
  ]
}

/** Splits identity options back into the two wire lists, de-duplicated. */
export const toCacAssignment = (
  identities: UsersGroupsOption[],
): CACAssignment => {
  const collect = (type: ExceptionType) =>
    Array.from(
      new Set(
        identities
          .filter(identity => identity.type === type)
          .map(identity => identity.emailId),
      ),
    )

  return {
    users: collect(ExceptionType.User),
    groups: collect(ExceptionType.Group),
  }
}

export const setCacValueAssignment = (
  assignments: CACAssignments | undefined,
  value: string,
  next: CacValueAssignment,
): CACAssignments => ({
  ...(assignments ?? {}),
  [value]: isAssignedToEveryone(next)
    ? CAC_ASSIGNMENT_ALL
    : {
        users: Array.from(new Set(next.users)),
        groups: Array.from(new Set(next.groups)),
      },
})

export const removeCacValueAssignment = (
  assignments: CACAssignments | undefined,
  value: string,
): CACAssignments => {
  if (!assignments?.[value]) {
    return {...(assignments ?? {})}
  }

  const {[value]: _removed, ...rest} = assignments
  return rest
}

/**
 * Rebuilds the map for a new value list, keeping each existing entry and
 * defaulting unseen values to everyone.
 */
export const rebuildCacAssignments = (
  app: CloudAppType | undefined,
  nextValues: string[],
): CACAssignments =>
  Object.fromEntries(
    nextValues.map(value => [
      value,
      app?.assignments?.[value] ?? CAC_ASSIGNMENT_ALL,
    ]),
  )

const isEmptyRestrictedAssignment = (assignment: CacValueAssignment): boolean =>
  !isAssignedToEveryone(assignment) &&
  assignment.users.length === 0 &&
  assignment.groups.length === 0

/**
 * The schema forbids an entry with both lists empty, so a value left
 * restricted with no identities means everyone.
 */
export const normalizeCacAssignments = (
  assignments: CACAssignments | undefined,
): CACAssignments =>
  Object.fromEntries(
    Object.entries(assignments ?? {}).map(([value, assignment]) => [
      value,
      isEmptyRestrictedAssignment(assignment) ? CAC_ASSIGNMENT_ALL : assignment,
    ]),
  )

/** Restores everyone on an app when restricted values still have no identities. */
export const discardEmptyRestrictedAssignments = (
  data: CloudAppResponseType | undefined,
  appId: string,
): CloudAppResponseType | undefined => {
  const app = data?.cac?.[appId]
  if (!data || !app) {
    return data
  }

  const assignments = normalizeCacAssignments(app.assignments)
  if (isEqual(assignments, app.assignments)) {
    return data
  }

  return {
    ...data,
    cac: {
      ...data.cac,
      [appId]: {
        ...app,
        assignments,
      },
    },
  }
}

/**
 * Empty restricted assignments serialize as everyone, so they are not unsaved
 * work until a user or group is added.
 */
export const hasCloudAppUnsavedChanges = (
  data: CloudAppResponseType | undefined,
  snapshot: CloudAppResponseType | undefined,
): boolean =>
  !isEqual(
    data && {
      ...data,
      cac: serializeCacForSave(data.cac),
    },
    snapshot && {
      ...snapshot,
      cac: serializeCacForSave(snapshot.cac),
    },
  )

/** The schema requires `consumer_ai_allowed` for Google and O365. */
const serializeIdpControls = (
  appId: CacGlobalSettingsAppId,
  controls: CacIdpAdditionalControls,
): CacIdpAdditionalControls => {
  const {aiControl} = CAC_GLOBAL_SETTINGS_CONFIG[appId]
  const restriction = controls[aiControl] ?? CACRestriction.Allow
  return {
    consumer_login_allowed: controls.consumer_login_allowed,
    consumer_upload_allowed: controls.consumer_upload_allowed,
    [aiControl]: restriction,
    consumer_ai_allowed:
      controls.consumer_ai_allowed &&
      isConsumerAiConfigurable(controls.consumer_login_allowed, restriction),
  }
}

export const isCacIdpAdditionalControls = (
  controls: CloudAppAdditionalControls,
): controls is CacIdpAdditionalControls =>
  'consumer_ai_allowed' in controls &&
  typeof controls.consumer_ai_allowed === 'boolean'

export const serializeAdditionalControls = (
  appId: string,
  controls: CloudAppAdditionalControls | undefined,
): CloudAppAdditionalControls | undefined => {
  if (!controls) return controls
  const {consumer_login_allowed, consumer_upload_allowed} = controls

  if (supportsCacGlobalSettings(appId)) {
    if (!isCacIdpAdditionalControls(controls)) {
      throw new Error(
        `consumer_ai_allowed is required for ${appId} additional controls`,
      )
    }
    return serializeIdpControls(appId, controls)
  }
  if (appId === 'github') {
    return {
      consumer_login_allowed,
      consumer_upload_allowed,
      ssh_over_https_allowed: true,
    }
  }
  return {consumer_login_allowed, consumer_upload_allowed}
}

export const serializeCacForSave = (
  cac: CloudAppListTypes | undefined,
): CloudAppSaveList =>
  Object.fromEntries(
    Object.entries(cac ?? {}).map(([appId, app]): [string, CloudAppSaveApp] => {
      return [
        appId,
        {
          ...app,
          assignments: normalizeCacAssignments(app.assignments),
          ...(app.additional_controls
            ? {
                additional_controls:
                  serializeAdditionalControls(appId, app.additional_controls) ??
                  app.additional_controls,
              }
            : {}),
        },
      ]
    }),
  )

export interface PreparedCloudAppSave {
  requestCac: CloudAppSaveList | 'base'
  saved: CloudAppResponseType
}

/** Builds the exact request and the state represented by that request. */
export const prepareCloudAppSave = (
  data: CloudAppResponseType,
  baseCloudApp: CloudAppResponseType | undefined,
  canInheritFromBase: boolean,
): PreparedCloudAppSave => {
  const requestCac = serializeCacForSave(data.cac)
  const shouldUseBase =
    canInheritFromBase &&
    baseCloudApp !== undefined &&
    isEqual(requestCac, serializeCacForSave(baseCloudApp.cac))

  if (shouldUseBase) {
    const baseCac = serializeCacForSave(baseCloudApp.cac)
    return {
      requestCac: 'base',
      saved: {inheritsCAC: true, cac: baseCac},
    }
  }

  return {
    requestCac,
    saved: {...data, inheritsCAC: false, cac: requestCac},
  }
}
